import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import {
  doc,
  setDoc,
  collection,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { Context as HomeContext } from "../../context/HomeContext";
import { dashboardService } from "../../api/dashboardService";

const broadcastFields = [
  { name: "event_subject", label: "Event subject", required: true },
  { name: "event_date", label: "Event date", half: true },
  { name: "event_time", label: "Event time", placeholder: "3 PM", half: true },
  { name: "event_location", label: "Location" },
  { name: "event_description", label: "Message / event description", required: true },
  { name: "ticket_price", label: "Ticket price", placeholder: "$45 or Free", half: true },
  { name: "button_name", label: "Button name", placeholder: "Book now", half: true },
  { name: "event_url", label: "Event link", placeholder: "https://example.com/event", half: true },
];

const emptyBroadcast = Object.fromEntries(
  broadcastFields.map(({ name }) => [name, ""]),
);

const StatCard = ({ title, value, loading, onClick }) => (
  <Card
    onClick={onClick}
    sx={{
      height: "100%",
      background: "#ffffff",
      border: "1px solid rgba(0, 0, 0, 0.12)",
      boxShadow: "none",
      cursor: onClick ? "pointer" : "default",
      transition: "all 0.2s ease-in-out",
      "&:hover": onClick
        ? {
            borderColor: "#00d1e3",
            backgroundColor: "#f5fcfd",
            transform: "translateY(-2px)",
          }
        : {},
    }}
  >
    <CardContent>
      <Typography
        variant="subtitle1"
        sx={{
          color: "#005b7d",
          mb: 2,
        }}
      >
        {title}
      </Typography>

      {loading ? (
        <CircularProgress
          size={28}
          thickness={5}
          sx={{ color: "#009fd9", mt: 1 }}
        />
      ) : (
        <Typography variant="h3" fontWeight="bold" sx={{ color: "#000000" }}>
          {value !== undefined && value !== null ? value : 0}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const Home = () => {
  const { state, fetchDashboardData } = useContext(HomeContext);
  const navigate = useNavigate();

  const [timeFilter, setTimeFilter] = useState(
    () => localStorage.getItem("dashboard_timeFilter") || "today",
  );
  const [startDate, setStartDate] = useState(
    () => localStorage.getItem("dashboard_startDate") || "",
  );
  const [endDate, setEndDate] = useState(
    () => localStorage.getItem("dashboard_endDate") || "",
  );

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const [draftStartDate, setDraftStartDate] = useState("");
  const [draftEndDate, setDraftEndDate] = useState("");

  const [dynamicMessageCount, setDynamicMessageCount] = useState(null);
  const [isMessageLoading, setIsMessageLoading] = useState(false);

  const [isBroadcastOpen, setIsBroadcastOpen] = useState(false);
  const [broadcastData, setBroadcastData] = useState(emptyBroadcast);
  const formattedDescription = broadcastData.event_description
    .replace(/\r\n?/g, "\n")
    .trim()
    .replace(/\n(?:[\t ]*\n)*/g, "\n\n");
  const formattedEventDate = broadcastData.event_date
    ? new Date(`${broadcastData.event_date}T00:00:00`).toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
      })
    : "";
  const canSendBroadcast = broadcastData.event_subject.trim() &&
    broadcastData.event_description.trim();
  const broadcastMessage = [
    broadcastData.event_subject.trim(),
    formattedDescription,
    [
      formattedEventDate && `Date: ${formattedEventDate}`,
      broadcastData.event_time.trim() && `Time: ${broadcastData.event_time.trim()}`,
      broadcastData.event_location.trim() && `Location: ${broadcastData.event_location.trim()}`,
    ].filter(Boolean).join("\n"),
    broadcastData.ticket_price.trim() && `Tickets: ${broadcastData.ticket_price.trim()}`,
    broadcastData.event_url.trim() && `${broadcastData.button_name.trim() || "Event details"}: ${broadcastData.event_url.trim()}`,
  ].filter(Boolean).join("\n\n");
  const [isBroadcasting, setIsBroadcasting] = useState(false);
  const [broadcastStatus, setBroadcastStatus] = useState({ type: "", msg: "" });

  const todayDateStr = new Date().toISOString().split("T")[0];

  useEffect(() => {
    localStorage.setItem("dashboard_timeFilter", timeFilter);
    localStorage.setItem("dashboard_startDate", startDate);
    localStorage.setItem("dashboard_endDate", endDate);
  }, [timeFilter, startDate, endDate]);

  useEffect(() => {
    const loadData = () => {
      if (timeFilter === "custom" && (!startDate || !endDate)) return;
      fetchDashboardData(timeFilter, startDate, endDate);
    };

    loadData();
    const pollInterval = setInterval(loadData, 60000);
    return () => clearInterval(pollInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeFilter, startDate, endDate]);

  useEffect(() => {
    const fetchFilteredMessages = async () => {
      if (timeFilter === "custom" && (!startDate || !endDate)) return;

      setIsMessageLoading(true);
      const res = await dashboardService.getMessageCount(
        timeFilter,
        startDate,
        endDate,
      );
      if (res.status) {
        setDynamicMessageCount(res.data.total_messages);
      }
      setIsMessageLoading(false);
    };

    if (state.stats.profiles) {
      fetchFilteredMessages();
    }
  }, [timeFilter, startDate, endDate, state.stats.profiles]);

  const openCalendar = () => {
    setDraftStartDate(startDate);
    setDraftEndDate(endDate);
    setIsCalendarOpen(true);
  };

  const handleFilterChange = (event) => {
    const value = event.target.value;
    if (value === "custom") {
      openCalendar();
    } else if (value === "total") {
      setTimeFilter(value);
      setStartDate("2026-01-01");
      setEndDate(todayDateStr);
    } else {
      setTimeFilter(value);
      setStartDate("");
      setEndDate("");
    }
  };

  const handleApplyCustomRange = () => {
    setStartDate(draftStartDate);
    setEndDate(draftEndDate);
    setTimeFilter("custom");
    setIsCalendarOpen(false);
  };

  const handleClear = () => {
    setTimeFilter("today");
    setStartDate("");
    setEndDate("");
    setDraftStartDate("");
    setDraftEndDate("");
  };

  const getDisplayTitle = () => {
    const titles = {
      today: "Today",
      past7Days: "Past 7 Days",
      past30Days: "Past 30 Days",
      total: "Total",
      custom: "Custom Range",
    };
    return titles[timeFilter] || "Today";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  const handleNewJoinClick = () => {
    navigate("/new-join-members", {
      state: { timeFilter, startDate, endDate },
    });
  };

  const handleSendBroadcast = async () => {
    if (!canSendBroadcast || isBroadcasting) return;
    if (broadcastData.event_url.trim()) {
      try {
        const url = new URL(broadcastData.event_url.trim());
        if (!["http:", "https:"].includes(url.protocol)) throw new Error();
      } catch {
        setBroadcastStatus({ type: "error", msg: "Enter a valid event link starting with https:// or http://." });
        return;
      }
    }
    setIsBroadcasting(true);
    setBroadcastStatus({ type: "", msg: "" });
    let chatBroadcastSent = false;

    try {
      const chatRef = doc(db, "web_chat_list", "global_broadcast");
      await setDoc(
        chatRef,
        {
          last_message: broadcastMessage,
          last_timestamp: serverTimestamp(),
          is_broadcast: true,
          users: ["admin_social_paws", "all"],
        },
        { merge: true },
      );

      const messagesRef = collection(chatRef, "messages");
      await addDoc(messagesRef, {
        sender_id: "admin_social_paws",
        receiver_id: "all",
        message: broadcastMessage,
        timestamp: serverTimestamp(),
        is_read: false,
        file: null,
      });
      chatBroadcastSent = true;

      const response = await dashboardService.sendBroadcastEmail(
        Object.fromEntries(Object.entries(broadcastData).map(([key, value]) => [
          key, key === "event_date" ? formattedEventDate
            : key === "event_description" ? formattedDescription : value.trim(),
        ])),
      );
      const emailResult = response.data?.data;
      if (response.data?.success !== true || emailResult?.success === false) {
        throw new Error("Email broadcast was not successful.");
      }

      // The chat message is already sent; prevent resubmitting it from this form.
      setBroadcastData(emptyBroadcast);
      if (Number(emailResult?.failed) > 0) {
        setBroadcastStatus({
          type: "warning",
          msg: `Chat broadcast sent, but email delivery failed for ${emailResult.failed} recipients.`,
        });
        return;
      }

      setBroadcastStatus({
        type: "success",
        msg: "Chat broadcast sent and email broadcast accepted successfully!",
      });
      setTimeout(() => {
        setIsBroadcastOpen(false);
        setBroadcastData(emptyBroadcast);
        setBroadcastStatus({ type: "", msg: "" });
      }, 2000);
    } catch (err) {
      console.error("Broadcast Error: ", err);
      if (chatBroadcastSent) setBroadcastData(emptyBroadcast);
      setBroadcastStatus({
        type: "error",
        msg: chatBroadcastSent
          ? "Chat broadcast sent, but the email broadcast could not be confirmed. Check delivery before sending again."
          : "An error occurred while broadcasting.",
      });
    } finally {
      setIsBroadcasting(false);
    }
  };

  const handleBroadcastKeyDown = (e) => {
    if (e.key === "Tab") {
      e.preventDefault();

      const start = e.target.selectionStart;
      const end = e.target.selectionEnd;

      const spaces = "    ";
      const newValue =
        broadcastData.event_description.substring(0, start) +
        spaces +
        broadcastData.event_description.substring(end);

      setBroadcastData((previous) => ({ ...previous, event_description: newValue }));

      setTimeout(() => {
        if (e.target) {
          e.target.selectionStart = e.target.selectionEnd =
            start + spaces.length;
        }
      }, 0);
    }
  };

  if (state.isLoading && !state.stats.profiles) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (state.error && !state.stats.profiles) {
    return <Alert severity="error">{state.error}</Alert>;
  }

  if (!state.stats.profiles) return null;

  return (
    <Box
      sx={{
        maxWidth: "1200px",
        margin: "0 auto",
        pb: 4,
        pt: 2,
        px: { xs: 2, md: 3 },
      }}
    >
      <Box
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", md: "center" }}
        gap={2}
        mb={4}
      >
        <Typography variant="h4" fontWeight="bold" sx={{ color: "#009fd9" }}>
          Social Paws Dashboard
        </Typography>

        <Box
          display="flex"
          flexDirection="column"
          alignItems={{ xs: "stretch", sm: "flex-end" }}
          gap={2}
          width={{ xs: "100%", md: "auto" }}
        >
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            gap={2}
          >
            {timeFilter === "custom" && startDate && endDate && (
              <Typography
                variant="subtitle2"
                sx={{
                  cursor: "pointer",
                  color: "#005b7d",
                  fontWeight: "bold",
                  textDecoration: "underline",
                  textAlign: { xs: "center", sm: "left" },
                  "&:hover": { color: "#009fd9" },
                }}
                onClick={openCalendar}
              >
                {formatDate(startDate)} - {formatDate(endDate)}
              </Typography>
            )}

            <FormControl
              size="small"
              sx={{ minWidth: 150, width: { xs: "100%", sm: "auto" } }}
            >
              <Select
                value={timeFilter}
                onChange={handleFilterChange}
                sx={{ backgroundColor: "#fff" }}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="past7Days">Past 7 Days</MenuItem>
                <MenuItem value="past30Days">Past 30 Days</MenuItem>
                <MenuItem value="total">Total</MenuItem>
                <MenuItem value="custom" onClick={openCalendar}>
                  Custom
                </MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              onClick={handleClear}
              sx={{
                textTransform: "uppercase",
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Clear
            </Button>
          </Box>

          <Button
            variant="contained"
            sx={{
              backgroundColor: "#009fd9",
              width: { xs: "100%", sm: "auto" },
            }}
            onClick={() => setIsBroadcastOpen(true)}
          >
            Send Broadcast Message
          </Button>
        </Box>
      </Box>

      <Box mb={6}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          sx={{ borderBottom: "1px solid rgba(0, 0, 0, 0.12)", pb: 1 }}
        >
          <Typography variant="h5" fontWeight="bold">
            Community Members
          </Typography>
          <Button variant="outlined" onClick={() => navigate("/select-members")}>
            Select Members
          </Button>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <StatCard
              title={`ALL - ${getDisplayTitle()}`}
              value={state.stats.profiles?.allUsers}
              loading={state.isLoading}
              onClick={handleNewJoinClick}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard
              title={`Pup Pawrents - ${getDisplayTitle()}`}
              value={state.stats.profiles?.teamPaws}
              loading={state.isLoading}
              onClick={handleNewJoinClick}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard
              title={`Pup Pals - ${getDisplayTitle()}`}
              value={state.stats.profiles?.teamSocial}
              loading={state.isLoading}
              onClick={handleNewJoinClick}
            />
          </Grid>
        </Grid>
      </Box>

      <Box mb={4}>
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ mb: 3, borderBottom: "1px solid rgba(0, 0, 0, 0.12)", pb: 1 }}
        >
          Platform Activity
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <StatCard
              title={`Messages Sent - ${getDisplayTitle()}`}
              value={
                dynamicMessageCount !== null
                  ? dynamicMessageCount
                  : state.stats.messages
              }
              loading={state.isLoading || isMessageLoading}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard
              title={`Treats Tossed - ${getDisplayTitle()}`}
              value={state.stats.treats}
              loading={state.isLoading}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <StatCard
              title={`Walks Booked - ${getDisplayTitle()}`}
              value={state.stats.walks}
              loading={state.isLoading}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Broadcast Message Modal */}
      <Dialog
        open={isBroadcastOpen}
        onClose={() => !isBroadcasting && setIsBroadcastOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: "bold", color: "#005b7d" }}>
          Send Broadcast Message
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Share event details with all Social Paws users by chat and email.
            Users will not be able to reply to the chat broadcast.
          </Typography>
          <Grid container spacing={2}>
            {broadcastFields.map(({ name, label, required, placeholder, half }, index) => (
              <Grid item xs={12} sm={half ? 6 : 12} key={name}>
                <TextField
                  autoFocus={index === 0}
                  margin="dense"
                  label={label}
                  required={required}
                  placeholder={placeholder}
                  type={name === "event_date" ? "date" : name === "event_url" ? "url" : "text"}
                  InputLabelProps={name === "event_date" ? { shrink: true } : undefined}
                  fullWidth
                  multiline={name === "event_description"}
                  minRows={name === "event_description" ? 6 : undefined}
                  maxRows={name === "event_description" ? 20 : undefined}
                  value={broadcastData[name]}
                  onChange={(e) => setBroadcastData((previous) => ({ ...previous, [name]: e.target.value }))}
                  onKeyDown={name === "event_description" ? handleBroadcastKeyDown : undefined}
                  disabled={isBroadcasting}
                />
              </Grid>
            ))}
          </Grid>
          {broadcastMessage && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "#f5fcfd", borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Chat message preview</Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                {broadcastMessage}
              </Typography>
            </Box>
          )}
          {broadcastStatus.msg && (
            <Alert severity={broadcastStatus.type} sx={{ mt: 2 }}>
              {broadcastStatus.msg}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setIsBroadcastOpen(false)}
            disabled={isBroadcasting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendBroadcast}
            variant="contained"
            sx={{ backgroundColor: "#009fd9" }}
            disabled={!canSendBroadcast || isBroadcasting}
          >
            {isBroadcasting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Send Broadcast"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isCalendarOpen} onClose={() => setIsCalendarOpen(false)}>
        <DialogTitle>Select Custom Date Range</DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            flexDirection={{ xs: "column", sm: "row" }}
            gap={3}
            mt={1}
          >
            <TextField
              label="Begin Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={draftStartDate}
              onChange={(e) => setDraftStartDate(e.target.value)}
              inputProps={{ max: todayDateStr }}
              fullWidth
            />
            <TextField
              label="End Date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={draftEndDate}
              onChange={(e) => setDraftEndDate(e.target.value)}
              inputProps={{ min: draftStartDate, max: todayDateStr }}
              disabled={!draftStartDate}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsCalendarOpen(false);
              if (!startDate || !endDate) setTimeFilter("today");
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApplyCustomRange}
            variant="contained"
            disabled={!draftStartDate || !draftEndDate}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Home;
